/**
 * Public-facing marketing content rendered below the existing splash
 * screen on ``/`` and ``/en``. School admins, hagwon admins, and
 * teachers visiting the site scroll past the splash and land here.
 *
 * Scope and structure are pinned by the design brief (Claude Design
 * handoff bundle, 2026-05-02). All copy lives in i18n; this file is
 * structure + interaction only.
 *
 * Sections, in scroll order:
 *   1. Value-proposition / inversion diagram (gradient continues)
 *   2. Post-lecture demonstration moment (stacked header + dual demo)
 *   3. Audience role selector (school / teacher / hagwon tablist)
 *   4. Trust band (PIPA, processing location, transparency)
 *   5. How it works (3 steps)
 *   6. Pilot CTA
 *   7. Footer
 *
 * Several blocks are deliberately commented out and tracked under
 * follow-up issues. Search for the all-caps tags to locate each:
 *
 *   INCORPORATION-PENDING   — issue #182, uncomment when 사업자등록 done
 *   OPERATOR-INFO-PENDING   — footer CPO + registered-office rows gated
 *                             after the previous operator left the
 *                             project; re-enable when a new CPO is
 *                             designated and a new registered address
 *                             exists
 *
 * The commented blocks contain real (or placeholder) markup so that
 * uncommenting is a one-step change. Do NOT delete them.
 *
 * CONSENT-FLOW-DISABLED retired in #185 follow-up. The original
 * plan (D2C-style in-app guardian consent capture) was reshaped in
 * #128 / #441 to a **school-as-controller** posture: institutions
 * discharge Art. 22-2 with guardians at enrollment (off-platform);
 * eehae records the institution's warranty as an agreement acceptance
 * and surfaces an Art. 22-2 §3 notice to students at first
 * login. The school-card bullet1 now describes that shipped flow
 * instead of the dropped in-app capture plan.
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiPost } from '../api/client'
import { useT, useLang } from '../i18n'
import LanguageSwitcher from './LanguageSwitcher'
// CSS Module — component-internal styles. The seven section roots
// also carry a ``data-marketing-section="N"`` attribute so
// ``snap.css`` and ``App.tsx``'s keyboard handler can target them
// without depending on the (now hashed) class names. See #264 PR B.
import s from './MarketingExtension.module.css'

interface NavLink {
  href: string
  labelKey:
    | 'mkt.nav.problem'
    | 'mkt.nav.audiences'
    | 'mkt.nav.trust'
    | 'mkt.nav.how'
}

const NAV_LINKS: NavLink[] = [
  { href: '#problem', labelKey: 'mkt.nav.problem' },
  { href: '#audiences', labelKey: 'mkt.nav.audiences' },
  { href: '#trust', labelKey: 'mkt.nav.trust' },
  { href: '#how', labelKey: 'mkt.nav.how' },
]

/**
 * Sticky marketing nav that fades in once the splash scrolls out of
 * view. Uses an IntersectionObserver on a sentinel placed at the
 * bottom of the splash so the threshold is independent of the
 * splash's height (which can change with viewport).
 */
function useStickyNavVisible(): boolean {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const sentinel = document.getElementById('mkt-splash-sentinel')
    if (!sentinel) return
    // Direct ``rect.top`` check rather than IntersectionObserver: the
    // sentinel is a zero-height element sitting exactly at the
    // splash/marketing boundary, which is the worst-case edge for
    // IntersectionObserver (some browsers report it as ``isIntersecting``
    // at the boundary, some don't). At the S1 snap target the sentinel
    // lands *exactly* at viewport top, so the IO version left the nav
    // hidden until the next snap nudged scroll a hair further. The
    // 200px lead time triggers the nav reveal partway through the
    // splash→S1 snap so it eases in alongside the scroll instead of
    // popping in at the end.
    const update = () => {
      setVisible(sentinel.getBoundingClientRect().top < 200)
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [])
  return visible
}

export default function MarketingExtension() {
  const t = useT()
  const navVisible = useStickyNavVisible()
  const choreo = useChoreographyEnabled()
  const choreoChunk = useChoreographyChunk(choreo)

  return (
    <>
      {/* Sentinel for the sticky nav. Sits at the very top of the
          marketing block, immediately below the splash. When this
          is visible (i.e. you're still on the splash), the nav
          stays hidden. When you scroll past it, the nav reveals. */}
      <div id="mkt-splash-sentinel" aria-hidden="true" />

      <StickyNav visible={navVisible} t={t} />

      {choreo && choreoChunk ? (
        <choreoChunk.default
          sections={{
            s1: <Section1ValueProp t={t} />,
            s2: <Section2PostLecture t={t} />,
            s3: <Section3Audiences t={t} />,
            s4: <Section4Trust t={t} />,
            s5: <Section5HowItWorks t={t} />,
            s6: <Section6PilotCta t={t} />,
            s7: <Section7Footer t={t} />,
          }}
        />
      ) : (
        <StackedSections t={t} />
      )}
    </>
  )
}

// Plain top-to-bottom layout. The default for touch / short-viewport /
// reduced-motion contexts, and the progressive-enhancement base the
// scroll choreography layers on top of.
// In the stacked / reduced-motion fallback the choreography scroll
// handler that drives ``mkt-footer-on-purple`` never runs, so the
// privacy footer's ``mix-blend-mode: difference`` would re-green over
// the violet sections — the splash gradient and the pilot ``.s6``,
// which share the same ``#3730a3 → #5b21b6 → #7c3aed`` violet. Flag the
// class from real geometry: set it whenever one of those violet
// elements spans the footer strip's vertical band. The cream
// mid-sections and the dark ``.s7`` footer keep the blend (difference
// reads correctly on both); ``.s1`` is excluded because its gradient is
// cream at the bottom, where the strip actually sits. The matching CSS
// override (shared with the choreography path) lives in
// PrivacyFooter.module.css.
function useStackedFooterOnPurple() {
  useEffect(() => {
    const VIOLET_SELECTOR = '.splash, [data-marketing-section="6"]'
    const update = () => {
      // Sample a touch above the strip's 12px baseline so we track the
      // backdrop actually behind the ~12px text, not the viewport edge.
      const stripY = window.innerHeight - 20
      const onPurple = Array.from(
        document.querySelectorAll(VIOLET_SELECTOR),
      ).some((el) => {
        const r = el.getBoundingClientRect()
        return r.top <= stripY && r.bottom >= stripY
      })
      document.documentElement.classList.toggle(
        'mkt-footer-on-purple',
        onPurple,
      )
    }
    update()
    // Re-run once after the next paint as well: the synchronous call
    // above is normally enough (SplashScreen mounts before this), but
    // it guarantees the geometry read happens after layout has settled.
    // Crucially, cancel it on unmount — if a resize switches to
    // ChoreographedSections before the frame fires, a late update()
    // would toggle ``mkt-footer-on-purple`` from stale stacked geometry
    // and stomp the class the choreography handler now owns.
    const rafId = window.requestAnimationFrame(update)
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      window.cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      document.documentElement.classList.remove('mkt-footer-on-purple')
    }
  }, [])
}

function StackedSections({ t }: { t: ReturnType<typeof useT> }) {
  useStackedFooterOnPurple()
  return (
    <>
      <Section1ValueProp t={t} />
      <Section2PostLecture t={t} />
      <Section3Audiences t={t} />
      <Section4Trust t={t} />
      <Section5HowItWorks t={t} />
      <Section6PilotCta t={t} />
      <Section7Footer t={t} />
    </>
  )
}


// Enable the choreography only where pinned full-viewport crossfades
// behave: pointer/large viewports tall enough to hold a section, and
// only when the visitor hasn't asked for reduced motion. Everything
// else gets StackedSections.
// Test-time escape hatch. Playwright's visual project sets this flag
// via ``addInitScript`` so the visual baselines capture the static
// stacked layout instead of the dynamic choreography (whose
// opacity-tied sections trip ``scrollIntoViewIfNeeded``). Set before
// the React tree mounts; checked synchronously by the gate so the
// initial render already lands on ``StackedSections``.
declare global {
  interface Window {
    __forceStackedMarketing__?: boolean
  }
}

function useChoreographyEnabled(): boolean {
  const [enabled, setEnabled] = useState(false)
  useEffect(() => {
    // Test-time override wins outright.
    if (window.__forceStackedMarketing__) {
      setEnabled(false)
      return
    }
    // Three runtime gates: large viewport, fine-pointer input (so
    // wheel / arrow keys are the dominant scroll modality), AND no
    // reduced-motion preference. The reduced-motion check uses
    // ``matchMedia`` directly rather than Motion's
    // ``useReducedMotion`` because the latter can return ``null``
    // before its internal media-query listener fires, and
    // ``!null === true`` would leave choreography enabled on a
    // reduced-motion visitor.
    //
    // Touch *capability* alone no longer disqualifies. #1083 also
    // gated on ``maxTouchPoints === 0``, which excluded every
    // touchscreen laptop even when driven by a trackpad / mouse —
    // the common Windows-hybrid case, where the choreography then
    // silently never appeared. ``(hover: hover) and (pointer: fine)``
    // already establishes that the *primary* input is a mouse, so a
    // pure tablet / phone (coarse primary pointer) still falls back
    // to StackedSections. On a mouse-primary hybrid, occasional
    // touch-drag scrolling degrades gracefully: native scroll still
    // drives the scrollY-tied crossfade; only the one-gesture-per-
    // section snap (wheel / keyboard only) is absent.
    const sizeMq = window.matchMedia(
      '(min-width: 1024px) and (min-height: 720px)',
    )
    const finePointerMq = window.matchMedia(
      '(hover: hover) and (pointer: fine)',
    )
    const reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () =>
      setEnabled(
        sizeMq.matches && finePointerMq.matches && !reduceMq.matches,
      )
    update()
    sizeMq.addEventListener('change', update)
    finePointerMq.addEventListener('change', update)
    reduceMq.addEventListener('change', update)
    return () => {
      sizeMq.removeEventListener('change', update)
      finePointerMq.removeEventListener('change', update)
      reduceMq.removeEventListener('change', update)
    }
  }, [])
  return enabled
}

// The choreography chunk carries ``motion``, so it loads only once the
// gate passes. StackedSections stays mounted until it arrives, and for
// good if the chunk fails to load or if the visitor has scrolled past half
// the splash at any point before it arrives, since the swap would remount
// every section and reset their state (#1505).
type ChoreographyModule = typeof import('./MarketingChoreography')

function useChoreographyChunk(enabled: boolean): ChoreographyModule | null {
  const [chunk, setChunk] = useState<ChoreographyModule | null>(null)
  const scrolledInRef = useRef(false)
  useEffect(() => {
    if (!enabled || chunk || scrolledInRef.current) return
    let cancelled = false
    const checkScroll = () => {
      if (window.scrollY >= window.innerHeight / 2) scrolledInRef.current = true
    }
    checkScroll()
    window.addEventListener('scroll', checkScroll, { passive: true })
    import('./MarketingChoreography').then(
      (m) => {
        checkScroll()
        window.removeEventListener('scroll', checkScroll)
        if (!cancelled && !scrolledInRef.current) setChunk(m)
      },
      () => {},
    )
    return () => {
      cancelled = true
      window.removeEventListener('scroll', checkScroll)
    }
  }, [enabled, chunk])
  return chunk
}


// ── Sticky nav ────────────────────────────────────────────────────

interface NavProps {
  visible: boolean
  t: ReturnType<typeof useT>
}

function StickyNav({ visible, t }: NavProps) {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  return (
    <nav
      className={[s.nav, visible && s.isVisible].filter(Boolean).join(' ')}
      aria-label="Site navigation"
    >
      <button
        type="button"
        className={s.navLogo}
        onClick={scrollToTop}
        aria-label="eehae"
      >
        eehae
      </button>
      <div className={s.navLinks}>
        {NAV_LINKS.map((link) => (
          <a key={link.href} href={link.href}>
            {t(link.labelKey)}
          </a>
        ))}
        <a href="#pilot" className={s.navCta}>
          {t('mkt.nav.cta')}
        </a>
      </div>
    </nav>
  )
}

// ── Section 1 — value proposition / inversion diagram ─────────────

function Section1ValueProp({ t }: { t: ReturnType<typeof useT> }) {
  return (
    <section className={s.s1} data-marketing-section="1" id="problem">
      <div className={s.s1Inner}>
        <div className={`${s.eyebrow} ${s.eyebrowOnGradient}`}>
          {t('mkt.s1.eyebrow')}
        </div>
        <h2 className={s.s1Headline}>
          {t('mkt.s1.headline.line1')}
          <br />
          {t('mkt.s1.headline.line2')}{' '}
          <span className={s.accent}>{t('mkt.s1.headline.accent')}</span>
          {t('mkt.s1.headline.line3')}
        </h2>
        <p className={s.s1Body}>{t('mkt.s1.body')}</p>

        <div className={s.diagram}>
          <DiagramSide
            label={t('mkt.s1.diagram.beforeLabel')}
            titleLine1={t('mkt.s1.diagram.beforeTitle.line1')}
            titleLine2={t('mkt.s1.diagram.beforeTitle.line2')}
            svg={<DiagramBefore teacherLabel={t('mkt.s1.diagram.teacher')} />}
          />
          <div className={s.diagramArrow} aria-hidden="true">
            <span className={s.diagramArrowText}>
              {t('mkt.s1.diagram.invertLabel')}
            </span>
          </div>
          <DiagramSide
            label={t('mkt.s1.diagram.afterLabel')}
            titleLine1={t('mkt.s1.diagram.afterTitle.line1')}
            titleLine2={t('mkt.s1.diagram.afterTitle.line2')}
            svg={
              <DiagramAfter
                teacherLabel={t('mkt.s1.diagram.teacher')}
                teacherUnderLabel={t('mkt.s1.diagram.teacherUnder')}
                studentsUnderLabel={t('mkt.s1.diagram.studentsUnder')}
                lecturingLabel={t('mkt.s1.diagram.lecturing')}
                tutoringLabel={t('mkt.s1.diagram.tutoring')}
              />
            }
          />
        </div>
      </div>
    </section>
  )
}

interface DiagramSideProps {
  label: string
  titleLine1: string
  titleLine2: string
  svg: React.ReactNode
}

function DiagramSide({ label, titleLine1, titleLine2, svg }: DiagramSideProps) {
  return (
    <div className={s.diagramSide}>
      <div className={s.diagramLabel}>{label}</div>
      {svg}
      <div className={s.diagramTitle}>
        {titleLine1}
        <br />
        {titleLine2}
      </div>
    </div>
  )
}

function DiagramBefore({ teacherLabel }: { teacherLabel: string }) {
  // Stylized SVG: one teacher in the center connected to many
  // student dots arranged in a circle around them. Self-contained;
  // colors come from currentColor + hardcoded brand tokens since
  // the SVG sits on the gradient background.
  return (
    <svg
      viewBox="0 0 320 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={s.diagramSvg}
    >
      <circle cx="160" cy="120" r="22" fill="#fde68a" />
      <text
        x="160"
        y="125"
        textAnchor="middle"
        fontFamily="Outfit"
        fontSize="11"
        fontWeight="600"
        fill="#5b21b6"
      >
        {teacherLabel}
      </text>
      <g stroke="rgba(255,255,255,0.35)" strokeWidth="1">
        <line x1="160" y1="120" x2="48" y2="48" />
        <line x1="160" y1="120" x2="160" y2="36" />
        <line x1="160" y1="120" x2="272" y2="48" />
        <line x1="160" y1="120" x2="36" y2="120" />
        <line x1="160" y1="120" x2="284" y2="120" />
        <line x1="160" y1="120" x2="48" y2="192" />
        <line x1="160" y1="120" x2="160" y2="204" />
        <line x1="160" y1="120" x2="272" y2="192" />
      </g>
      <g fill="rgba(255,255,255,0.85)">
        <circle cx="48" cy="48" r="10" />
        <circle cx="160" cy="36" r="10" />
        <circle cx="272" cy="48" r="10" />
        <circle cx="36" cy="120" r="10" />
        <circle cx="284" cy="120" r="10" />
        <circle cx="48" cy="192" r="10" />
        <circle cx="160" cy="204" r="10" />
        <circle cx="272" cy="192" r="10" />
      </g>
    </svg>
  )
}

function DiagramAfter({
  teacherLabel,
  teacherUnderLabel,
  studentsUnderLabel,
  lecturingLabel,
  tutoringLabel,
}: {
  teacherLabel: string
  teacherUnderLabel: string
  studentsUnderLabel: string
  lecturingLabel: string
  tutoringLabel: string
}) {
  // Stylized SVG: the teacher (left, gold) lectures the same column
  // of students (center) that eehae (right, violet) provides parallel
  // 1:1 tutoring to. Two top labels — LECTURING above the teacher's
  // arrows, 1:1 TUTORING above eehae's — frame the dual-channel
  // framing: eehae sits *alongside* the teacher's lecturing role
  // rather than replacing it. Adapted from the Claude Design
  // "Elevator Pitch" with-eehae SVG.
  return (
    <svg
      viewBox="0 0 320 220"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={s.diagramSvg}
    >
      <defs>
        <marker
          id="diagramAfterArrYel"
          viewBox="0 0 8 8"
          refX="6"
          refY="4"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          <path d="M0,1 L6,4 L0,7 Z" fill="rgba(253,230,138,0.92)" />
        </marker>
        <marker
          id="diagramAfterArrVio"
          viewBox="0 0 8 8"
          refX="6"
          refY="4"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          {/* Light lavender (~Tailwind violet-200) so the arrowheads
              read against the s1 purple gradient — violet-300 was
              still too close in hue to the background to pop. The
              eehae node keeps its darker violet-400 fill as the
              saturated anchor; arrows lighten as they travel toward
              students, creating clear figure-ground separation. */}
          <path d="M0,1 L6,4 L0,7 Z" fill="rgba(221,214,254,1)" />
        </marker>
      </defs>

      {/* Top labels framing each side's role */}
      <text
        x="95"
        y="18"
        textAnchor="middle"
        fontFamily="JetBrains Mono"
        fontSize="10"
        fill="rgba(253,230,138,0.92)"
        letterSpacing="2"
        fontWeight="600"
      >
        {lecturingLabel}
      </text>
      <text
        x="220"
        y="18"
        textAnchor="middle"
        fontFamily="JetBrains Mono"
        fontSize="10"
        fill="rgba(167,139,250,0.98)"
        letterSpacing="2"
        fontWeight="600"
      >
        {tutoringLabel}
      </text>

      {/* Brackets under top labels */}
      <path
        d="M 40 26 L 40 32 L 150 32 L 150 26"
        stroke="rgba(253,230,138,0.45)"
        strokeWidth="1"
        fill="none"
      />
      <path
        d="M 170 26 L 170 32 L 280 32 L 280 26"
        stroke="rgba(167,139,250,0.5)"
        strokeWidth="1"
        fill="none"
      />

      {/* Teacher node (left, vertically centered on the student column) */}
      <circle cx="22" cy="120" r="15" fill="#fde68a" />
      <text
        x="22"
        y="125"
        textAnchor="middle"
        fontFamily="Outfit"
        fontSize="13"
        fontWeight="700"
        fill="#5b21b6"
      >
        {teacherLabel}
      </text>
      <text
        x="22"
        y="200"
        textAnchor="middle"
        fontFamily="Outfit"
        fontSize="9"
        fill="rgba(255,255,255,0.78)"
        fontWeight="600"
        letterSpacing="1.5"
      >
        {teacherUnderLabel}
      </text>

      {/* Teacher → students (gold lecture arrows, fanned to the column) */}
      <g stroke="rgba(253,230,138,0.7)" strokeWidth="1.3" fill="none">
        <line x1="37" y1="120" x2="150" y2="60" markerEnd="url(#diagramAfterArrYel)" />
        <line x1="37" y1="120" x2="150" y2="90" markerEnd="url(#diagramAfterArrYel)" />
        <line x1="37" y1="120" x2="150" y2="120" markerEnd="url(#diagramAfterArrYel)" />
        <line x1="37" y1="120" x2="150" y2="150" markerEnd="url(#diagramAfterArrYel)" />
        <line x1="37" y1="120" x2="150" y2="180" markerEnd="url(#diagramAfterArrYel)" />
      </g>

      {/* 5 students in a vertical column */}
      <g fill="rgba(255,255,255,0.94)">
        <circle cx="160" cy="60" r="9" />
        <circle cx="160" cy="90" r="9" />
        <circle cx="160" cy="120" r="9" />
        <circle cx="160" cy="150" r="9" />
        <circle cx="160" cy="180" r="9" />
      </g>
      <text
        x="160"
        y="200"
        textAnchor="middle"
        fontFamily="Outfit"
        fontSize="9"
        fill="rgba(255,255,255,0.78)"
        fontWeight="600"
        letterSpacing="1.5"
      >
        {studentsUnderLabel}
      </text>

      {/* eehae → students (violet 1:1 tutoring arrows, fanned from the right) */}
      <g stroke="rgba(221,214,254,0.95)" strokeWidth="1.3" fill="none">
        <line x1="283" y1="120" x2="170" y2="60" markerEnd="url(#diagramAfterArrVio)" />
        <line x1="283" y1="120" x2="170" y2="90" markerEnd="url(#diagramAfterArrVio)" />
        <line x1="283" y1="120" x2="170" y2="120" markerEnd="url(#diagramAfterArrVio)" />
        <line x1="283" y1="120" x2="170" y2="150" markerEnd="url(#diagramAfterArrVio)" />
        <line x1="283" y1="120" x2="170" y2="180" markerEnd="url(#diagramAfterArrVio)" />
      </g>

      {/* eehae node (right) */}
      <rect
        x="283"
        y="100"
        width="32"
        height="40"
        rx="9"
        fill="rgba(167,139,250,0.98)"
        stroke="rgba(255,255,255,0.6)"
        strokeWidth="0.7"
      />
      <text
        x="299"
        y="124"
        textAnchor="middle"
        fontFamily="Outfit"
        fontSize="11"
        fontWeight="650"
        fill="#fff"
        letterSpacing="-0.2"
      >
        eehae
      </text>
    </svg>
  )
}

// ── Section 2 — post-lecture demonstration moment ─────────────────

function Section2PostLecture({ t }: { t: ReturnType<typeof useT> }) {
  // Pulse mockup uses real /api/classes/{code}/pulse field labels +
  // illustrative-but-plausible numbers. NOT real components — this is
  // a static marketing illustration that happens to mirror the real
  // dashboard's metric shape so the visual matches what teachers
  // would actually see post-pilot.
  return (
    <section className={s.s2} data-marketing-section="2">
      <div className={s.s2Inner}>
        <div className={s.s2Header}>
          <h2 className={s.s2Headline}>{t('mkt.s2.headline')}</h2>
          <div className={s.s2Body}>
            <p>
              {t('mkt.s2.body.p1.lead')}
              <strong>{t('mkt.s2.body.p1.strong')}</strong>
            </p>
            <p>{t('mkt.s2.body.p2')}</p>
            <p>{t('mkt.s2.body.p3')}</p>
          </div>
        </div>

        <div className={s.split}>
          <div className={s.splitArrow} aria-hidden="true">
            →
          </div>

          {/* Student-side panel */}
          <div className={s.panel}>
            <div className={s.panelTag}>{t('mkt.s2.studentTag')}</div>
            <div className={s.panelTitle}>{t('mkt.s2.studentPanelTitle')}</div>
            <div className={s.chatMock}>
              <div className={`${s.chatBubble} ${s.ai}`}>{t('mkt.s2.chat.ai1')}</div>
              <div className={`${s.chatBubble} ${s.student}`}>
                {t('mkt.s2.chat.student1')}
              </div>
              <div className={`${s.chatBubble} ${s.ai}`}>{t('mkt.s2.chat.ai2')}</div>
              <div className={`${s.chatBubble} ${s.student}`}>
                {t('mkt.s2.chat.student2')}
              </div>
              <div className={`${s.chatBubble} ${s.ai}`}>{t('mkt.s2.chat.ai3')}</div>
              <div className={s.chatMeta}>{t('mkt.s2.chat.meta')}</div>
            </div>
          </div>

          {/* Teacher-side panel */}
          <div className={s.panel}>
            <div className={s.panelTag}>{t('mkt.s2.teacherTag')}</div>
            <div className={s.panelTitle}>
              {t('mkt.s2.teacherPanelTitle')} ·{' '}
              <span className={s.livePill}>{t('mkt.s2.live')}</span>
            </div>
            <div className={s.dashMock}>
              <div className={s.dashPulseRow}>
                <PulseStat
                  label={t('mkt.s2.pulse.active')}
                  value={t('mkt.s2.pulse.activeValue')}
                  variant="default"
                />
                <PulseStat
                  label={t('mkt.s2.pulse.sessions')}
                  value={t('mkt.s2.pulse.sessionsValue')}
                  variant="default"
                />
                <PulseStat
                  label={t('mkt.s2.pulse.mastery')}
                  value={t('mkt.s2.pulse.masteryValue')}
                  variant="default"
                />
              </div>
              <div className={s.dashAttentionRow}>
                <span className={s.attentionLabel}>
                  {t('mkt.s2.pulse.attention')}
                </span>
                <span className={s.attentionNum}>
                  {t('mkt.s2.pulse.attentionValue')}
                </span>
                <span className={s.attentionUnit}>
                  {t('mkt.s2.pulse.attentionUnit')}
                </span>
                <span className={s.attentionAvatars} aria-hidden="true">
                  <span className={s.attentionAvatar}>민</span>
                  <span className={s.attentionAvatar}>서</span>
                  <span className={s.attentionAvatar}>준</span>
                  <span className={s.attentionAvatar}>윤</span>
                </span>
              </div>
              {/* Mini sparkline placeholder. Real dashboard renders a
                  Sparkline component over sessions_per_day; here we
                  hardcode a path so the page is self-contained. */}
              <div className={s.dashSparkline} aria-hidden="true">
                <svg viewBox="0 0 200 32" preserveAspectRatio="none">
                  <polyline
                    points="0,24 28,20 56,22 84,12 112,16 140,8 168,14 200,6"
                    fill="none"
                    stroke="var(--violet-600, #7c3aed)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

interface PulseStatProps {
  label: string
  value: string
  variant?: 'default' | 'ok' | 'warn' | 'bad'
}

function PulseStat({ label, value, variant = 'default' }: PulseStatProps) {
  // Map BEM variant to the module class. Pre-#264 PR B this was
  // ``mkt-pulse-stat-num--${variant}`` interpolated directly; under
  // CSS Modules the class names are hashed so we look them up via
  // the module object.
  const variantClass = {
    default: s.pulseStatNumDefault,
    ok: s.pulseStatNumOk,
    warn: s.pulseStatNumWarn,
    bad: s.pulseStatNumBad,
  }[variant]
  return (
    <div className={s.pulseStat}>
      <div className={s.pulseStatLabel}>{label}</div>
      <div className={`${s.pulseStatNum} ${variantClass}`}>{value}</div>
    </div>
  )
}

// ── Section 3 — three audiences ───────────────────────────────────

interface AudienceCardData {
  variant: 'school' | 'teacher' | 'hagwon'
  icon: React.ReactNode
}

const AUDIENCE_CARDS: AudienceCardData[] = [
  { variant: 'school', icon: <SchoolIcon /> },
  { variant: 'teacher', icon: <TeacherIcon /> },
  { variant: 'hagwon', icon: <HagwonIcon /> },
]

function Section3Audiences({ t }: { t: ReturnType<typeof useT> }) {
  // Role selector — the three audiences (school / teacher / hagwon)
  // share one panel that swaps on tab select instead of sitting in
  // three side-by-side cards. Denser, and it lets a visitor jump
  // straight to their own context. Implemented as an ARIA tablist
  // (arrow-key navigable); every panel stays in the DOM and toggles
  // via the ``hidden`` attribute, so nothing is hidden from crawlers.
  const [active, setActive] = useState(0)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const dir = e.key === 'ArrowRight' ? 1 : -1
    const next = (active + dir + AUDIENCE_CARDS.length) % AUDIENCE_CARDS.length
    setActive(next)
    tabRefs.current[next]?.focus()
  }

  return (
    <section className={s.s3} data-marketing-section="3" id="audiences">
      <div className={s.s3Inner}>
        <h2 className={s.s3Headline}>{t('mkt.s3.headline')}</h2>

        <div className={s.audiencePicker}>
          <div
            className={s.audienceTabs}
            role="tablist"
            aria-label={t('mkt.nav.audiences')}
            onKeyDown={onKeyDown}
          >
            {AUDIENCE_CARDS.map((card, i) => {
              const labelKey =
                `mkt.s3.${card.variant}.label` as Parameters<typeof t>[0]
              return (
                <button
                  key={card.variant}
                  ref={(el) => {
                    tabRefs.current[i] = el
                  }}
                  type="button"
                  role="tab"
                  id={`audience-tab-${card.variant}`}
                  aria-selected={active === i}
                  aria-controls={`audience-panel-${card.variant}`}
                  tabIndex={active === i ? 0 : -1}
                  className={[s.audienceTab, active === i && s.audienceTabActive]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setActive(i)}
                >
                  <span className={s.audienceTabIcon} aria-hidden="true">
                    {card.icon}
                  </span>
                  {t(labelKey)}
                </button>
              )
            })}
          </div>

          {AUDIENCE_CARDS.map((card, i) => {
            const k = (suffix: string) =>
              `mkt.s3.${card.variant}.${suffix}` as Parameters<typeof t>[0]
            return (
              <div
                key={card.variant}
                role="tabpanel"
                id={`audience-panel-${card.variant}`}
                aria-labelledby={`audience-tab-${card.variant}`}
                // ``tabIndex={0}`` on the active panel only — the
                // hidden siblings don't render any focusable content
                // and skipping them keeps the tab order clean. With
                // this, keyboard users who select a tab in the strip
                // can tab forward INTO the panel content (static
                // copy + bullets), matching the ARIA Authoring
                // Practices Guide pattern for static-content tabs.
                // CodeRabbit (outside-diff) on PR #1083.
                tabIndex={active === i ? 0 : -1}
                hidden={active !== i}
                className={s.audiencePanel}
              >
                <h3 className={s.audiencePanelHeadline}>{t(k('headline'))}</h3>
                <ul className={s.audiencePanelBullets}>
                  <li>{t(k('bullet1'))}</li>
                  <li>{t(k('bullet2'))}</li>
                  <li>{t(k('bullet3'))}</li>
                  <li>{t(k('bullet4'))}</li>
                </ul>
                <p className={s.audiencePanelFit}>
                  <span className={s.audienceFitLabel}>{t(k('fitLabel'))}</span>
                  {t(k('fit'))}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

// ── Section 4 — trust pillars ─────────────────────────────────────

interface TrustPillarData {
  icon: React.ReactNode
  titleKey: 'mkt.s4.pillar1.title' | 'mkt.s4.pillar2.title' | 'mkt.s4.pillar3.title'
  subtitleKey:
    | 'mkt.s4.pillar1.subtitle'
    | 'mkt.s4.pillar2.subtitle'
    | 'mkt.s4.pillar3.subtitle'
  bodyKey: 'mkt.s4.pillar1.body' | 'mkt.s4.pillar2.body' | 'mkt.s4.pillar3.body'
}

const TRUST_PILLARS: TrustPillarData[] = [
  {
    icon: <ShieldIcon />,
    titleKey: 'mkt.s4.pillar1.title',
    subtitleKey: 'mkt.s4.pillar1.subtitle',
    bodyKey: 'mkt.s4.pillar1.body',
  },
  {
    icon: <GlobeIcon />,
    titleKey: 'mkt.s4.pillar2.title',
    subtitleKey: 'mkt.s4.pillar2.subtitle',
    bodyKey: 'mkt.s4.pillar2.body',
  },
  {
    icon: <DocumentIcon />,
    titleKey: 'mkt.s4.pillar3.title',
    subtitleKey: 'mkt.s4.pillar3.subtitle',
    bodyKey: 'mkt.s4.pillar3.body',
  },
]

function Section4Trust({ t }: { t: ReturnType<typeof useT> }) {
  // Three trust guarantees grouped into one bordered band split by
  // hairlines, rather than three free-standing cards. Reads as a
  // single compact unit and drops the decorative 01/02/03 numbering.
  return (
    <section className={s.s4} data-marketing-section="4" id="trust">
      <div className={s.s4Inner}>
        <div className={s.s4Header}>
          <h2 className={s.s4Headline}>{t('mkt.s4.headline')}</h2>
        </div>

        <div className={s.trustBand}>
          {TRUST_PILLARS.map((p) => (
            <div key={p.titleKey} className={s.trustCell}>
              <div className={s.trustIcon}>{p.icon}</div>
              <h3 className={s.trustPillarTitle}>
                {t(p.titleKey)}
                <span className={s.trustPillarSubtitle}>
                  {t(p.subtitleKey)}
                </span>
              </h3>
              <p className={s.trustPillarBody}>{t(p.bodyKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── Section 5 — how it works ──────────────────────────────────────

function Section5HowItWorks({ t }: { t: ReturnType<typeof useT> }) {
  return (
    <section className={s.s5} data-marketing-section="5" id="how">
      <div className={s.s5Inner}>
        <div className={s.s5Header}>
          <h2 className={s.s5Headline}>{t('mkt.s5.headline')}</h2>
        </div>

        <div className={s.steps}>
          <Step num={1} titleKey="mkt.s5.step1.title" bodyKey="mkt.s5.step1.body" t={t} icon={<UploadIcon />} />
          <Step num={2} titleKey="mkt.s5.step2.title" bodyKey="mkt.s5.step2.body" t={t} icon={<ChatIcon />} />
          <Step num={3} titleKey="mkt.s5.step3.title" bodyKey="mkt.s5.step3.body" t={t} icon={<DashboardIcon />} />
        </div>
      </div>
    </section>
  )
}

interface StepProps {
  num: number
  titleKey:
    | 'mkt.s5.step1.title'
    | 'mkt.s5.step2.title'
    | 'mkt.s5.step3.title'
  bodyKey:
    | 'mkt.s5.step1.body'
    | 'mkt.s5.step2.body'
    | 'mkt.s5.step3.body'
  t: ReturnType<typeof useT>
  icon: React.ReactNode
}

function Step({ num, titleKey, bodyKey, t, icon }: StepProps) {
  return (
    <div className={s.step}>
      <div className={s.stepCircle}>{num}</div>
      <h3 className={s.stepTitle}>{t(titleKey)}</h3>
      <p className={s.stepBody}>{t(bodyKey)}</p>
      <div className={s.stepIllust} aria-hidden="true">
        {icon}
      </div>
    </div>
  )
}

// ── Section 6 — pilot CTA ─────────────────────────────────────────

function Section6PilotCta({ t }: { t: ReturnType<typeof useT> }) {
  return (
    <section className={s.s6} data-marketing-section="6" id="pilot">
      <div className={s.s6Inner}>
        <div>
          <h2 className={s.s6Headline}>{t('mkt.s6.headline')}</h2>
          <p className={s.s6Body}>{t('mkt.s6.body')}</p>
          <div className={s.s6Meta}>
            <div className={s.s6MetaItem}>
              <div className={s.s6MetaLabel}>
                {t('mkt.s6.metaPeriodLabel')}
              </div>
              <div className={s.s6MetaValue}>
                {t('mkt.s6.metaPeriodValue')}
              </div>
            </div>
            <div className={s.s6MetaItem}>
              <div className={s.s6MetaLabel}>
                {t('mkt.s6.metaScopeLabel')}
              </div>
              <div className={s.s6MetaValue}>
                {t('mkt.s6.metaScopeValue')}
              </div>
            </div>
          </div>
        </div>

        <PilotForm t={t} />
      </div>
    </section>
  )
}

/**
 * Pilot-inquiry form — wired in #181.
 *
 * Submits to ``POST /api/marketing/pilot-inquiry`` (see
 * ``V3/backend/routers/marketing.py``). The backend sends two
 * transactional emails via Resend: a founder notification to
 * ``contact@eehae.app`` and an acknowledgment to the submitter
 * from ``no-reply@eehae.app``. No DB persistence — the inquiry
 * lives in inboxes only.
 *
 * Spam protection (layered):
 *   - slowapi rate limit (5/hour per IP) — backend side, see
 *     ``rate_limit.py``.
 *   - Honeypot field ``hp_website`` — hidden via the
 *     ``s.honeypot`` CSS class. Bots that auto-fill every input
 *     trip it; the backend silent-200s without sending email.
 *   - Field length caps in the backend Pydantic schema.
 *
 * Consent disclosure: PIPA Art. 15 §2 requires the user to be told
 * what's collected, why, and for how long. The inline note below
 * the submit button does that + links to the full privacy policy.
 * Submitting the form is the consent action.
 */
function PilotForm({ t }: { t: ReturnType<typeof useT> }) {
  const lang = useLang()
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (submitting) return
    const fd = new FormData(e.currentTarget)
    setSubmitting(true)
    setError(null)
    try {
      await apiPost('/marketing/pilot-inquiry', {
        institution: fd.get('org'),
        contact_name: fd.get('name'),
        role: fd.get('role'),
        email: fd.get('email'),
        phone: fd.get('phone') || null,
        message: fd.get('msg') || null,
        // Pass the current app locale so the acknowledgment email
        // template matches the language the submitter is using.
        locale: lang,
        // Honeypot — should always be empty for real users (the
        // field is visually + a11y hidden via s.honeypot). Bots
        // that auto-fill every input fill it; backend silent-200s
        // without sending email.
        hp_website: fd.get('hp_website') || null,
      })
      setSubmitted(true)
    } catch (err: unknown) {
      setError(
        (err instanceof Error && err.message) ||
          t('mkt.s6.form.errorGeneric'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className={`${s.pilotForm} ${s.pilotFormSuccess}`}>
        <div className={s.pfSuccessIcon} aria-hidden="true">✓</div>
        <h4>{t('mkt.s6.form.successTitle')}</h4>
        <p>{t('mkt.s6.form.successBody')}</p>
      </div>
    )
  }

  return (
    <form className={s.pilotForm} ref={formRef} onSubmit={handleSubmit} noValidate>
      <div className={s.pfTitle}>{t('mkt.s6.formTitle')}</div>
      <div className={s.pfHint}>{t('mkt.s6.formHint')}</div>
      <div className={s.pfGrid}>
        <div className={`${s.pfField} ${s.pfFieldFull}`}>
          <label htmlFor="pf-org">{t('mkt.s6.form.org')}</label>
          <input id="pf-org" name="org" type="text" placeholder={t('mkt.s6.form.orgPlaceholder')} required />
        </div>
        <div className={s.pfField}>
          <label htmlFor="pf-name">{t('mkt.s6.form.name')}</label>
          <input id="pf-name" name="name" type="text" placeholder={t('mkt.s6.form.namePlaceholder')} required />
        </div>
        <div className={s.pfField}>
          <label htmlFor="pf-role">{t('mkt.s6.form.role')}</label>
          <input id="pf-role" name="role" type="text" placeholder={t('mkt.s6.form.rolePlaceholder')} required />
        </div>
        <div className={s.pfField}>
          <label htmlFor="pf-email">{t('mkt.s6.form.email')}</label>
          <input id="pf-email" name="email" type="email" placeholder={t('mkt.s6.form.emailPlaceholder')} required />
        </div>
        <div className={s.pfField}>
          <label htmlFor="pf-phone">
            {t('mkt.s6.form.phone')}{' '}
            <span className={s.pfOptional}>{t('mkt.s6.form.phoneOptional')}</span>
          </label>
          <input id="pf-phone" name="phone" type="tel" placeholder={t('mkt.s6.form.phonePlaceholder')} />
        </div>
        <div className={`${s.pfField} ${s.pfFieldFull}`}>
          <label htmlFor="pf-msg">{t('mkt.s6.form.msg')}</label>
          <textarea id="pf-msg" name="msg" placeholder={t('mkt.s6.form.msgPlaceholder')} />
        </div>

        {/* Honeypot — visually + a11y hidden via .honeypot. Real
            users never see or interact with this; bots auto-filling
            every form input land here, which the backend uses as a
            "silently drop" signal. The label + autocomplete=off make
            the field look benign to a naive scraper. */}
        <div className={s.honeypot} aria-hidden="true">
          <label htmlFor="pf-hp-website">Website (do not fill)</label>
          <input
            id="pf-hp-website"
            name="hp_website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {error && <div className={s.pfError} role="alert">{error}</div>}
        <button type="submit" className={s.pfSubmit} disabled={submitting}>
          {submitting ? t('mkt.s6.form.submitting') : `${t('mkt.s6.form.submit')} →`}
        </button>

        {/* PIPA Art. 15 §2 inline consent disclosure. The privacy
            policy link opens the full §2 (collection items + retention)
            and §5 (cross-border transfers, including the Resend
            transactional-email processor). */}
        <p className={s.pfConsent}>
          {t('mkt.s6.form.consent')}{' '}
          <Link to="/privacy">{t('mkt.s6.form.consentLink')}</Link>
        </p>
      </div>
    </form>
  )
}

// ── Section 7 — footer ────────────────────────────────────────────

function Section7Footer({ t }: { t: ReturnType<typeof useT> }) {
  // Privacy + cookies links route through the same hrefs PrivacyFooter
  // uses elsewhere (see PrivacyFooter.tsx); the marketing footer
  // explicitly surfaces them rather than relying on the floating
  // link strip alone, since school admins read footers as the formal
  // signal of legal compliance.
  return (
    <footer className={s.s7} data-marketing-section="7">
      <div className={s.s7Inner}>
        <div className={s.s7Top}>
          <div className={s.s7BrandCol}>
            <span className={s.s7Brand}>eehae</span>
            <div className={s.s7Tagline}>{t('mkt.s7.tagline')}</div>
            <div className={s.s7Info}>
              {/* OPERATOR-INFO-PENDING: the previous CPO left the
                  project, so this row is gated by ``false`` until a
                  new CPO is designated (PIPA Art. 31). When that
                  happens, fill in ``mkt.s7.info.cpoValue`` in ko.ts /
                  en.ts and flip the gate to ``true`` (or remove the
                  wrapper). */}
              {/* eslint-disable-next-line no-constant-binary-expression */}
              {false && (
                <div>
                  <span className={s.s7InfoLabel}>
                    {t('mkt.s7.info.cpoLabel')}
                  </span>{' '}
                  {t('mkt.s7.info.cpoValue')}
                </div>
              )}

              {/* INCORPORATION-PENDING (#182): the JSX below is gated
                  by ``false`` so it stays in the tree but doesn't render.
                  When 사업자등록 completes, change ``false`` to ``true``
                  (or remove the wrapper); replace ``info.bizValueTbd``
                  with the real 사업자등록번호 in i18n. */}
              {/* eslint-disable-next-line no-constant-binary-expression */}
              {false && (
                <>
                  <div>
                    <span className={s.s7InfoLabel}>
                      {t('mkt.s7.info.companyLabel')}
                    </span>{' '}
                    {t('mkt.s7.info.companyValue')}
                  </div>
                  <div>
                    <span className={s.s7InfoLabel}>
                      {t('mkt.s7.info.bizLabel')}
                    </span>{' '}
                    {/* Replace with real 사업자등록번호 once known. */}
                    {t('mkt.s7.info.bizValueTbd')}
                  </div>
                </>
              )}

              {/* OPERATOR-INFO-PENDING: the registered office address
                  (formerly live per #183) was removed after the
                  previous operator's departure. Gate stays ``false``
                  until a new registered address exists — then fill in
                  ``mkt.s7.info.addressValue`` in ko.ts / en.ts and
                  flip the gate to ``true``. Independent of #182 — the
                  address can ship before 사업자등록 completes. */}
              {/* eslint-disable-next-line no-constant-binary-expression */}
              {false && (
                <div>
                  <span className={s.s7InfoLabel}>
                    {t('mkt.s7.info.addressLabel')}
                  </span>{' '}
                  {t('mkt.s7.info.addressValue')}
                </div>
              )}

              {/* contact@eehae.app provisioned (closes #184). */}
              <div>
                <span className={s.s7InfoLabel}>
                  {t('mkt.s7.info.contactLabel')}
                </span>{' '}
                <a href="mailto:contact@eehae.app">
                  {t('mkt.s7.info.contactValue')}
                </a>
              </div>
            </div>
          </div>

          <FooterCol
            heading={t('mkt.s7.col.product')}
            links={[
              { href: '#problem', label: t('mkt.s7.link.problem') },
              { href: '#audiences', label: t('mkt.s7.link.audiences') },
              { href: '#trust', label: t('mkt.s7.link.trust') },
              { href: '#how', label: t('mkt.s7.link.how') },
            ]}
          />

          <FooterCol
            heading={t('mkt.s7.col.legal')}
            links={[
              // Privacy + cookies anchor + CPO anchor all route to
              // /privacy with the matching #fragment. The marketing
              // footer uses raw <a> tags (not react-router <Link>),
              // so navigation is a hard-load and the browser handles
              // the native fragment scroll. Section ids:
              //   /privacy#cookies → <section id="cookies">
              //   /privacy#cpo     → <section id="cpo">
              { href: '/privacy', label: t('mkt.s7.link.privacy') },
              {
                href: '/privacy#cookies',
                label: t('mkt.s7.link.cookies'),
              },
              { href: '/privacy#cpo', label: t('mkt.s7.link.cpo') },
              // Terms of service — wired in issue #425 item 2.
              { href: '/terms', label: t('mkt.s7.link.terms') },
            ]}
          />

          <FooterCol
            heading={t('mkt.s7.col.contact')}
            links={[
              { href: '#pilot', label: t('mkt.s7.link.pilot') },
              // Press / partnerships link — commented out until we
              // have a real press contact target (probably the same
              // mailbox as #184's contact@eehae.app, OR a dedicated
              // press@eehae.app).
              // { href: 'mailto:press@eehae.app', label: t('mkt.s7.link.press') },
            ]}
          />
        </div>

        <div className={s.s7Bottom}>
          <div>{t('mkt.s7.copyright')}</div>
          <div className={s.s7BottomRight}>
            {/* Reuse the global LanguageSwitcher so the marketing
                footer's language toggle stays in sync with the rest
                of the app's language state. */}
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </footer>
  )
}

interface FooterColLink {
  href: string | null
  label: string
}

function FooterCol({
  heading,
  links,
}: {
  heading: string
  links: FooterColLink[]
}) {
  return (
    <div className={s.s7Col}>
      <h4>{heading}</h4>
      <ul>
        {links.map((link) => (
          <li key={link.label}>
            {link.href ? (
              <a href={link.href}>{link.label}</a>
            ) : (
              <span className={s.s7LinkDisabled} aria-disabled="true">
                {link.label}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── Inline icons (line-icon style, single-color via currentColor) ─

function SchoolIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 21h18" />
      <path d="M5 21V8l7-4 7 4v13" />
      <path d="M9 21v-6h6v6" />
      <path d="M9 11h.01" />
      <path d="M15 11h.01" />
    </svg>
  )
}

function TeacherIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h3" />
      <path d="M18 12h3" />
      <path d="M12 3v3" />
      <path d="M12 18v3" />
    </svg>
  )
}

function HagwonIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 9l10-5 10 5-10 5z" />
      <path d="M6 11v5c2 1.5 4 2 6 2s4-.5 6-2v-5" />
      <path d="M22 9v5" />
    </svg>
  )
}

function ShieldIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 4 L34 10 V20 C34 28 28 34 20 36 C12 34 6 28 6 20 V10 Z" />
      <path d="M14 20 L18 24 L26 16" />
    </svg>
  )
}

function GlobeIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="20" cy="20" r="14" />
      <path d="M6 20 H34" />
      <path d="M20 6 C24 10 26 14 26 20 C26 26 24 30 20 34" />
      <path d="M20 6 C16 10 14 14 14 20 C14 26 16 30 20 34" />
      <circle cx="20" cy="20" r="3" fill="currentColor" />
    </svg>
  )
}

function DocumentIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="6" y="8" width="28" height="24" rx="2" />
      <path d="M11 16 H29" />
      <path d="M11 22 H22" />
      <path d="M11 27 H18" />
      <circle cx="27" cy="27" r="3.5" fill="none" />
    </svg>
  )
}

function UploadIcon() {
  return (
    <svg
      width="80"
      height="60"
      viewBox="0 0 80 60"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    >
      <rect x="6" y="6" width="48" height="42" rx="3" fill="#ffffff" />
      <path
        d="M14 16 H42 M14 22 H38 M14 28 H32 M14 34 H40"
        stroke="currentColor"
        opacity="0.4"
      />
      <path
        d="M58 24 L70 24 M64 18 L70 24 L64 30"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ChatIcon() {
  return (
    <svg
      width="120"
      height="60"
      viewBox="0 0 120 60"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    >
      <rect x="8" y="14" width="46" height="20" rx="10" fill="#ffffff" />
      <path d="M14 22 H40 M14 26 H32" opacity="0.4" />
      <rect x="66" y="28" width="46" height="20" rx="10" fill="#ede9fe" />
      <path d="M72 36 H100 M72 40 H92" opacity="0.4" />
    </svg>
  )
}

function DashboardIcon() {
  return (
    <svg
      width="100"
      height="60"
      viewBox="0 0 100 60"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    >
      <rect x="6" y="6" width="88" height="48" rx="3" fill="#ffffff" />
      <rect
        x="14"
        y="16"
        width="22"
        height="6"
        rx="1"
        fill="currentColor"
        opacity="0.7"
      />
      <rect
        x="14"
        y="26"
        width="36"
        height="3"
        rx="1"
        fill="currentColor"
        opacity="0.3"
      />
      <rect
        x="14"
        y="32"
        width="28"
        height="3"
        rx="1"
        fill="currentColor"
        opacity="0.3"
      />
      <rect x="60" y="16" width="26" height="32" rx="2" fill="#ede9fe" />
      <path
        d="M64 40 L70 32 L76 36 L82 24"
        stroke="currentColor"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

